import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Lloro } from '../../types';

interface LloroWithUser extends Lloro {
  user_name: string;
  user_email: string;
}

const AdminLloros: React.FC = () => {
  const [lloros, setLloros] = useState<LloroWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLloros();
  }, []);

  const loadLloros = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getLloros();
      setLloros(response.data.data.lloros);
    } catch (err: any) {
      setError('Error al cargar registros de lloros');
    } finally {
      setIsLoading(false);
    }
  };

  const getIntensityColor = (intensity: string) => {
    switch (intensity) {
      case 'leve': return '#10b981';
      case 'moderado': return '#f59e0b';
      case 'fuerte': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getIntensityEmoji = (intensity: string) => {
    switch (intensity) {
      case 'leve': return '😢';
      case 'moderado': return '😭';
      case 'fuerte': return '😩';
      default: return '💧';
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return <div className="loading-container">Cargando registros...</div>;
  }

  return (
    <div className="admin-lloros">
      <h1>Todos los Lloros</h1>
      {error && <div className="error-message">{error}</div>}

      {lloros.length === 0 ? (
        <div className="empty-state">
          <p>No hay registros de lloros</p>
        </div>
      ) : (
        <div className="lloros-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Usuario</th>
                <th>Intensidad</th>
                <th>Duracion</th>
                <th>Causa</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {lloros.map((lloro) => (
                <tr key={lloro.id}>
                  <td>{formatDate(lloro.date)}</td>
                  <td>{lloro.time?.substring(0, 5) || '-'}</td>
                  <td>
                    <div className="user-info">
                      <span className="user-name">{lloro.user_name}</span>
                      <span className="user-email">{lloro.user_email}</span>
                    </div>
                  </td>
                  <td>
                    <span
                      className="intensity-badge"
                      style={{
                        backgroundColor: `${getIntensityColor(lloro.intensity)}20`,
                        color: getIntensityColor(lloro.intensity),
                        border: `1px solid ${getIntensityColor(lloro.intensity)}`
                      }}
                    >
                      {getIntensityEmoji(lloro.intensity)} {lloro.intensity}
                    </span>
                  </td>
                  <td>{lloro.duration || '-'}</td>
                  <td>{lloro.trigger_cause || '-'}</td>
                  <td className="notes-cell">{lloro.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .admin-lloros {
          padding: 20px;
        }

        .admin-lloros h1 {
          margin-bottom: 20px;
          color: var(--text-primary);
        }

        .lloros-table-container {
          overflow-x: auto;
        }

        .admin-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--surface);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: var(--card-shadow);
        }

        .admin-table th,
        .admin-table td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--border);
        }

        .admin-table th {
          background: var(--secondary);
          font-weight: 600;
          color: var(--text-primary);
        }

        .admin-table tr:hover {
          background: var(--secondary);
        }

        .user-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .user-name {
          font-weight: 500;
          color: var(--text-primary);
        }

        .user-email {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .intensity-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .notes-cell {
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary);
        }
      `}</style>
    </div>
  );
};

export default AdminLloros;
